import { CRM_CONTRACT_FILE_EXTENSIONS, CRM_FILE_MAX_SIZE_MB } from '@/Containers/CrmSection/Components/Contracts/CrmContract'
import { attachUploadedFiles } from '@/Ship/Helpers/attachUploadedFiles'
import { renderDateTimeView, renderUsersView } from '@/Ship/Helpers/fieldViews'

import { TRANSPORT_OFFER_COMMENT_MAX_LENGTH, TRANSPORT_OFFER_KINDS } from '../Contracts/ITransportOffer'
import { OFFER_FIELD_LABELS as L } from '../Contracts/OfferFieldLabels'
import { closeOfferCards } from '../Stores/OpenOfferCards'
import { OfferFormTemplate } from '../Templates/OfferFormTemplate'

import type { TransportOfferKind } from '../Contracts/ITransportOffer'
import type { IFieldConfig } from '@/Core/Contracts/UI/Form'
import type { IResourceConfig } from '@/Core/Contracts/UI/Resource'

/** Лише перегляд: ставить сервер. */
const readOnly = (field: IFieldConfig): IFieldConfig => ({
    ...field,
    isEditable: false,
    inputProps: { ...(field.inputProps ?? {}), disabled: true },
})

/** Ширина шухляди картки пропозиції — як у картки оплати заявки. */
const OFFER_DRAWER_WIDTH = 600

/**
 * Поля, яких бек від форми не приймає: аудит і зовнішній код (його пише лише
 * адміністратор через API, ПР-06). Власника шлемо лише при створенні (ПР-01).
 */
const READ_ONLY_KEYS = ['id', 'externalCode', 'createdAt', 'createdBy', 'updatedAt', 'updatedBy'] as const

/**
 * Збереження пропозиції закриває шухляду — для всіх трьох власників однаково
 * (`SPEC-024` §8.1; легасі закривав лише в заявки). Створення ядро закриває
 * саме, тож закриваємо лише після зміни — інакше закриття спрацювало б двічі.
 */
const editedForms = new WeakSet<object>()

interface SaveEvent {
    values: Record<string, unknown>
    formContext?: { values?: Record<string, unknown> }
}

interface SubmitEvent {
    formContext?: object
}

/**
 * Збирає ресурс пропозиції власника (`SPEC-024` §3.3): ті самі поля й картка
 * для запиту, тендера й заявки; відрізняються колекція, поле власника,
 * «Перевізник» (лише заявка) і файли (лише запит і тендер).
 *
 * Власного реєстру немає (без `pageUrl`): пропозиції живуть у блоці картки
 * власника, створюють їх лише звідти (`CREATED_FROM_PARENT`). Права — з беку
 * (`_permissions` запису, колекційні `create` і `chooseResponsible`).
 */
export const createOfferResource = (kind: TransportOfferKind): IResourceConfig => {
    const config = TRANSPORT_OFFER_KINDS[kind]

    const prepareOfferPayload = ({ values, formContext }: SaveEvent): void => {
        READ_ONLY_KEYS.forEach((key) => delete values[key])

        if (typeof formContext?.values?.['@id'] === 'string') {
            delete values[config.ownerField]
            editedForms.add(formContext)
        }
    }

    const closeAfterEdit = ({ formContext }: SubmitEvent): void => {
        if (!formContext || !editedForms.has(formContext)) return

        editedForms.delete(formContext)
        closeOfferCards(config.resource)
    }

    const fields: Record<string, IFieldConfig> = {
        ...(config.hasCarrier ? { carrier: { label: L.carrier, type: 'select', resource: 'Crm.Carrier' } } : {}),
        offeredPrice: {
            label: L.offeredPrice,
            type: 'number',
            isRequired: true,
            // Ціна має бути більшою за нуль (Q24.1) — бек відповість 422 на 0
            inputProps: { min: 0.01, precision: 2, style: { width: '100%' } },
        },
        offeredPriceCurrency: {
            label: L.offeredPriceCurrency,
            type: 'select',
            resource: 'Setting.ListCurrency',
            responseLabel: (item: { code?: unknown; title?: unknown }) =>
                typeof item?.code === 'string' ? item.code : String(item?.title ?? ''),
        },
        comment: {
            label: L.comment,
            type: 'textarea',
            inputProps: { maxLength: TRANSPORT_OFFER_COMMENT_MAX_LENGTH },
        },
        responsible: {
            label: L.responsible,
            type: 'select',
            resource: 'Setting.User',
            isRequired: true,
            view: renderUsersView,
        },
        ...(config.fileUploadUrl
            ? {
                  // Двокроково й при створенні: крок 1 — `<o>_offer_files`,
                  // крок 2 — `fileUuids` у POST/PATCH; білий список — як у договору CRM
                  files: {
                      label: L.files,
                      type: 'file',
                      isMultiple: true,
                      uploadUrl: config.fileUploadUrl,
                      uploadAccept: CRM_CONTRACT_FILE_EXTENSIONS,
                      uploadMaxSizeMb: CRM_FILE_MAX_SIZE_MB,
                      showViewIcon: false,
                      showEditIcon: false,
                  } as IFieldConfig,
              }
            : {}),
        createdAt: readOnly({ label: L.createdAt, type: 'datetime', view: renderDateTimeView }),
        updatedAt: readOnly({ label: L.updatedAt, type: 'datetime', view: renderDateTimeView }),
    }

    return {
        // Заголовок шухляди — однина: картка показує одну пропозицію (§8.2)
        title: L.cardTitle,
        fields,
        form: {
            eventHandlers: {
                ...(config.fileUploadUrl
                    ? { onBeforeTransformSendSaveRequest: { offerFileUuids: attachUploadedFiles('files') } }
                    : {}),
                onBeforeSendSaveRequest: { OfferPayload: prepareOfferPayload },
                onAfterSuccessSubmit: { OfferCloseAfterEdit: closeAfterEdit },
            },
        },
        detailConfig: {
            modeConfig: {
                view: { template: OfferFormTemplate },
                edit: { template: OfferFormTemplate },
                create: { template: OfferFormTemplate },
            },
        },
        drawer: {
            width: OFFER_DRAWER_WIDTH,
        },
        restUrl: config.restUrl,
    }
}
